import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, message, Space, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { favoriteService } from '../services/favoriteService';
import { coffeeService } from '../services/coffeeService';
import type { Favorite } from '../types/favorite';
import type { CoffeeBean } from '../types/coffee';
import axios from 'axios';

interface FavoritesModalProps {
    visible: boolean;
    userId: string;
    onClose: () => void;
}

const FavoritesModal: React.FC<FavoritesModalProps> = ({ visible, userId, onClose }) => {
    const [favorites, setFavorites] = useState<Array<Favorite & { coffee?: CoffeeBean }>>([]);
    const [loading, setLoading] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchFavorites = async () => {
        setLoading(true);
        try {
            const favoritesData = await favoriteService.getFavoritesByUserId(userId);
            const favoritesWithCoffee = await Promise.all(
                favoritesData.map(async (favorite) => {
                    const coffee = await coffeeService.getCoffeeById(favorite.coffeeId);
                    return { ...favorite, coffee };
                })
            );
            setFavorites(favoritesWithCoffee as (Favorite & { coffee?: CoffeeBean })[]);
        } catch (error) {
            console.error('Error fetching favorites:', error);
            message.error('Không thể tải danh sách yêu thích');
        }
        setLoading(false);
    };

    useEffect(() => {
        if (visible) {
            fetchFavorites();
        }
    }, [visible, userId]);

    const handleRemoveFavorite = async (favoriteId: string) => {
        try {
            await favoriteService.deleteFavorite(favoriteId);
            message.success('Đã xóa khỏi danh sách yêu thích');
            fetchFavorites();

            // Gọi api gửi dữ liệu yêu thích để AI tự động cập nhật
            // await axios.get(`https://coffee-ai.8am.vn/update`);
        } catch (error) {
            console.error('Error removing favorite:', error);
            message.error('Không thể xóa khỏi danh sách yêu thích');
        }
    };

    const columns = [
        {
            title: 'Hình ảnh',
            dataIndex: ['coffee', 'imageUrl'],
            key: 'imageUrl',
            render: (url: string) => url ? <img src={url} alt="coffee" style={{ width: 50 }} /> : null
        },
        {
            title: 'Tên cà phê',
            dataIndex: ['coffee', 'name'],
            key: 'name',
        },
        {
            title: 'Thông tin',
            dataIndex: ['coffee', 'beanInfo'],
            key: 'beanInfo',
            ellipsis: true,
        },
        {
            title: 'Ngày thêm',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: any) => {
                if (date?.seconds) {
                    return new Date(date.seconds * 1000).toLocaleDateString('vi-VN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                }
                return new Date(date).toLocaleDateString('vi-VN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_: any, record: Favorite) => (
                <Button
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => handleRemoveFavorite(record.id!)}
                />
            )
        }
    ];

    const getMobileColumns = () => [
        {
            title: 'Thông tin',
            dataIndex: 'coffee',
            key: 'info',
            render: (_: any, record: any) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {record.coffee?.imageUrl && (
                            <img src={record.coffee.imageUrl} alt="coffee" style={{ width: 100 }} />
                        )}
                        <div>
                            <strong>Tên cà phê:</strong>{' '}
                            <Tag>{record.coffee?.name || 'N/A'}</Tag>
                        </div>
                        <div>
                            <strong>Thông tin:</strong>{' '}
                            <div style={{
                                fontSize: '12px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 5,
                                WebkitBoxOrient: 'vertical'

                            }}>{record.coffee?.beanInfo}</div>
                        </div>
                        <div>
                            <strong>Ngày thêm:</strong>{' '}
                            <div style={{ fontSize: '12px' }}>
                                {record.createdAt?.seconds ?
                                    new Date(record.createdAt.seconds * 1000).toLocaleDateString('vi-VN') :
                                    new Date(record.createdAt).toLocaleDateString('vi-VN')}
                            </div>
                        </div>
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={() => handleRemoveFavorite(record.id!)}
                        />
                    </Space>
                </div>
            )
        }
    ];

    return (
        <Modal
            title="Danh sách cà phê yêu thích"
            visible={visible}
            onCancel={onClose}
            width={isMobile ? '100%' : 1000}
            footer={null}
        >
            <Table
                columns={isMobile ? getMobileColumns() : columns}
                dataSource={favorites}
                loading={loading}
                rowKey="id"
                pagination={{
                    defaultPageSize: isMobile ? 5 : 10,
                    position: isMobile ? ['bottomCenter'] : ['bottomRight']
                }}
            />
        </Modal>
    );
};

export default FavoritesModal;